import TaskFields, {
  TaskDraft,
} from "components/Dashboard/ManageTasksDialog/TaskFields";
import {
  keyOfTaskPerson,
  taskPeopleOf,
  useTaskPeople,
} from "./ActivityTabParts";
import { useReference } from "./hooks";
import { TASK_FINISHED } from "./kinds";
import { isSubjects, NOTE_LIMIT, RIGHT_AWAY } from "./model";
import {
  columnById,
  fieldByRef,
  nameOf,
  ReferenceData,
  roleOf,
} from "./reference";
import StepActionFields, {
  MarkAsField,
  NoteTextField,
  TutorsField,
} from "./StepActionFields";
import type {
  ApprovalItem,
  CloseStatus,
  CrmFieldStep,
  CrmMoveStep,
  PersonKey,
  PersonRole,
  Proposal,
  ProposalEdits,
  Trigger,
} from "./types";
import { fieldValueWords, subjectNames } from "./words";

/*
 * Change it first: a task or CRM card's fields, filled in with what it
 * would do. A task gets the task form's own fields; a CRM move or field
 * its step's fields. Only what changed is sent with the answer. A
 * message's wording is its template's, so a message card has none.
 */

type Of<A extends Proposal["action"]> = Extract<Proposal, { action: A }>;

export type ChangeDraft =
  | { action: "task"; task: TaskDraft }
  | { action: "crm_move"; step: CrmMoveStep }
  | { action: "crm_field"; step: CrmFieldStep }
  | { action: "close_task"; status: CloseStatus }
  | { action: "assign_tutor"; tutors: PersonKey[] }
  | { action: "add_note"; text: string; pinned: boolean };

/** The card's proposal as a draft to change (a message card has none). */
export function draftOf(
  ref: ReferenceData,
  item: ApprovalItem
): ChangeDraft | null {
  const p = item.proposal;
  // The step's fields edit a step; this one only carries the values.
  const step = {
    id: item.stepId,
    position: RIGHT_AWAY,
    askFirst: { on: false },
  };
  switch (p.action) {
    case "task":
      return {
        action: "task",
        task: {
          name: p.name,
          content: p.description,
          due_date: p.dueDate,
          assigned: taskPeopleOf(ref, p.assignees),
          tagged: taskPeopleOf(ref, p.about),
        },
      };
    case "message":
      return null;
    case "crm_move":
      return {
        action: "crm_move",
        step: {
          ...step,
          action: "crm_move",
          crmMove: {
            boardId: p.boardId,
            columnId: p.columnId,
            values: [...(p.values ?? [])],
          },
        },
      };
    case "crm_field":
      return {
        action: "crm_field",
        step: {
          ...step,
          action: "crm_field",
          crmField: p.mode
            ? {
                field: "subjects",
                mode: p.mode,
                subject_ids: Array.isArray(p.value) ? p.value : [],
              }
            : { field: p.field, value: p.value },
        },
      };
    case "close_task":
      return { action: "close_task", status: p.status };
    case "assign_tutor":
      return { action: "assign_tutor", tutors: [...p.tutors] };
    case "add_note":
      return { action: "add_note", text: p.text, pinned: p.pinned };
  }
}

const same = (a: PersonKey[], b: PersonKey[]) =>
  a.length === b.length && a.every((key) => b.includes(key));

function roleWords(role: PersonRole) {
  return role === "admin" ? "an admin" : `a ${role}`;
}

/**
 * What changed, as the answer's edits, or the sentence that says what is
 * missing first.
 */
export function editsOf(
  ref: ReferenceData,
  item: ApprovalItem,
  draft: ChangeDraft
): { edits: ProposalEdits } | { problem: string } {
  const p = item.proposal;
  if (draft.action === "task" && p.action === "task") {
    const name = draft.task.name.trim();
    if (!name || !draft.task.due_date || !draft.task.assigned.length)
      return {
        problem:
          "Add a task name, a due date and at least one person to assign it to.",
      };
    const assignees = draft.task.assigned.map(keyOfTaskPerson);
    const about = draft.task.tagged.map(keyOfTaskPerson);
    const edits: Partial<Omit<Of<"task">, "action">> = {};
    if (name !== p.name) edits.name = name;
    if (draft.task.content !== p.description)
      edits.description = draft.task.content;
    if (draft.task.due_date !== p.dueDate) edits.dueDate = draft.task.due_date;
    if (!same(assignees, p.assignees)) {
      edits.assignees = assignees;
      // Picked by hand now: no longer the admins because nobody fit.
      edits.fallback = false;
    }
    if (!same(about, p.about)) edits.about = about;
    return { edits };
  }
  if (draft.action === "crm_move" && p.action === "crm_move") {
    const { boardId, columnId } = draft.step.crmMove;
    const found = columnById(ref, boardId, columnId);
    if (!found) return { problem: "Pick a board and a column first." };
    const role = roleOf(p.person);
    if (found.column.role !== role)
      return {
        problem: `${nameOf(ref, p.person)} is ${roleWords(
          role
        )}, so pick a column that holds ${role}s.`,
      };
    const edits: Partial<Omit<Of<"crm_move">, "action">> = {};
    const values = draft.step.crmMove.values ?? [];
    if (found.board.id !== p.boardId || found.column.id !== p.columnId) {
      edits.boardId = found.board.id;
      edits.columnId = found.column.id;
      edits.columnLabel = found.column.label;
    }
    // A move's values go with its column.
    if (
      edits.columnId !== undefined ||
      JSON.stringify(values) !== JSON.stringify(p.values ?? [])
    )
      edits.values = values;
    return { edits };
  }
  if (draft.action === "crm_field" && p.action === "crm_field") {
    const config = draft.step.crmField;
    const role = roleOf(p.person);
    if (isSubjects(config)) {
      if (role !== "student" && role !== "tutor")
        return {
          problem: `${nameOf(ref, p.person)} is ${roleWords(
            role
          )}, and only students and tutors have subjects.`,
        };
      if (!config.subject_ids.length)
        return { problem: "Pick the subjects first." };
      const edits: Partial<Omit<Of<"crm_field">, "action">> = {};
      if (
        !p.mode ||
        config.mode !== p.mode ||
        JSON.stringify(config.subject_ids) !== JSON.stringify(p.value)
      ) {
        edits.field = "subjects";
        edits.fieldLabel = "Subjects";
        edits.mode = config.mode;
        edits.value = config.subject_ids;
        edits.valueLabel = subjectNames(ref, config.subject_ids);
      }
      return { edits };
    }
    const field = fieldByRef(ref, config.field);
    if (!field) return { problem: "Pick a field first." };
    if (field.role !== role)
      return {
        problem: `${nameOf(ref, p.person)} is ${roleWords(
          role
        )}, so pick a ${role}'s field.`,
      };
    const { value } = config;
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    )
      return { problem: `Pick a value for ${field.label} first.` };
    const edits: Partial<Omit<Of<"crm_field">, "action">> = {};
    if (field.field !== p.field || p.mode) {
      edits.field = field.field;
      edits.fieldLabel = field.label;
      edits.mode = undefined;
    }
    if (edits.field || JSON.stringify(value) !== JSON.stringify(p.value)) {
      edits.value = value;
      edits.valueLabel = fieldValueWords(ref, field, value);
    }
    return { edits };
  }
  if (draft.action === "close_task" && p.action === "close_task")
    return { edits: draft.status !== p.status ? { status: draft.status } : {} };
  if (draft.action === "assign_tutor" && p.action === "assign_tutor") {
    if (!draft.tutors.length) return { problem: "Choose the tutors." };
    return {
      edits: same(draft.tutors, p.tutors) ? {} : { tutors: draft.tutors },
    };
  }
  if (draft.action === "add_note" && p.action === "add_note") {
    if (!draft.text.trim()) return { problem: "Write the note." };
    if (draft.text.length > NOTE_LIMIT)
      return { problem: "Shorten the note to 5,000 characters." };
    return {
      edits:
        draft.text !== p.text || draft.pinned !== p.pinned
          ? { text: draft.text, pinned: draft.pinned }
          : {},
    };
  }
  return { edits: {} };
}

/** The people in context, listed first in the pickers: who it is about, their family and their tutors. */
function inContext(ref: ReferenceData, about: PersonKey | null) {
  return taskPeopleOf(
    ref,
    about
      ? [
          about,
          ...(ref.parentsOf[about] ?? []),
          ...(ref.studentsOfParent[about] ?? []),
          ...(ref.tutorsOf[about] ?? []),
        ]
      : []
  );
}

function TaskChange({
  item,
  draft,
  onChange,
  dateContainer,
}: {
  item: ApprovalItem;
  draft: Extract<ChangeDraft, { action: "task" }>;
  onChange: (next: ChangeDraft) => void;
  dateContainer: () => HTMLElement;
}) {
  const ref = useReference();
  const people = useTaskPeople();
  const context = inContext(ref, item.about);
  const me = ref.me ? taskPeopleOf(ref, [ref.me]) : [];
  return (
    <div className="task-form">
      <TaskFields
        idPrefix={`approval-${item.id}`}
        task={draft.task}
        onChange={(task) => onChange({ ...draft, task })}
        people={people}
        suggestedAssign={[...me, ...context]}
        suggestedAbout={[...context, ...me]}
        dateContainer={dateContainer}
      />
    </div>
  );
}

/**
 * A card's fields for Change it first, filled in. trigger: the automation's
 * own, which decides the columns and fields its people can have.
 * dateContainer: where a task's calendar opens (the page, or a dialog).
 */
export default function ActivityTabChange({
  item,
  draft,
  onChange,
  trigger,
  dateContainer = () => document.body,
}: {
  item: ApprovalItem;
  draft: ChangeDraft;
  onChange: (next: ChangeDraft) => void;
  trigger: Trigger | null;
  dateContainer?: () => HTMLElement;
}) {
  switch (draft.action) {
    case "task":
      return (
        <TaskChange
          item={item}
          draft={draft}
          onChange={onChange}
          dateContainer={dateContainer}
        />
      );
    case "crm_move":
    case "crm_field":
      return (
        <StepActionFields
          step={draft.step}
          onChange={(step) => {
            if (step.action === "crm_move")
              onChange({ action: step.action, step });
            if (step.action === "crm_field")
              onChange({ action: step.action, step });
          }}
          // A deleted automation's trigger is gone: every kind of person
          // is offered, and Add checks the person's own.
          trigger={trigger ?? { kind: TASK_FINISHED, options: {}, filters: [] }}
        />
      );
    case "close_task":
      return (
        <MarkAsField
          value={draft.status}
          onChange={(status) => onChange({ ...draft, status })}
        />
      );
    case "assign_tutor":
      return (
        <TutorsField
          label="Tutors"
          showLabel
          value={draft.tutors}
          onChange={(tutors) => onChange({ ...draft, tutors })}
        />
      );
    case "add_note":
      return (
        <NoteTextField
          value={draft.text}
          onChange={(text) => onChange({ ...draft, text })}
        />
      );
  }
}
